<script lang="ts">
import {
  SelectRoot,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  SelectItemText,
  SelectViewport,
} from 'reka-ui';

export default {
  name: 'UniversalSearch',
  components: {
    SelectRoot,
    SelectTrigger,
    SelectValue,
    SelectContent,
    SelectItem,
  },
};
</script>

<script setup lang="ts">
import { ref, computed, watch, inject, onMounted, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';
import { Store } from 'vuex';
import { universalSearchService } from '@/services/api';
import type { UniversalSearchResult, EntityType, EntityTypeOption } from '@/types/UniversalSearch';
import type { TabConfig } from '@/types/EntityTabs';
import { useEntityTabs } from '@/composables/useEntityTabs';
import type { AppState } from '@/store';
import { Building2Icon, BarChart3Icon, StoreIcon, SearchIcon, ChevronDown } from 'lucide-vue-next';
import { filters } from '@/utilities/Filters';

// -----
// data
// -----

const RESULT_LIMIT = 100;
const router = useRouter();
const store = inject<Store<AppState>>('store');
const { getTabs, loading: tabsLoading } = useEntityTabs();

// Reactive state
const searchQuery = ref('');
const selectedEntityType = ref<EntityType | 'all' | undefined>();
const results = ref<UniversalSearchResult[]>([]);
const hoveredResult = ref<UniversalSearchResult | null>(null);
const loading = ref(false);
const hasSearched = ref(false);
const entityTabs = ref<TabConfig[]>([]);
const searchResultsRef = ref<HTMLElement>();

// Non-reactive state
let searchTimeout: NodeJS.Timeout;

// Constants
const entityTypeOptions: EntityTypeOption[] = [
  { value: 'account', label: 'Clients', icon: Building2Icon },
  { value: 'carrier', label: 'Carriers', icon: BarChart3Icon },
  { value: 'wholesaler', label: 'Wholesalers', icon: StoreIcon },
];

// -----
// computed
// -----

const loadingTabs = computed(() => tabsLoading.value);

const shouldShowResults = computed(() => {
  return searchQuery.value.trim().length > 0 && (loading.value || results.value.length > 0 || hasSearched.value);
});

const searchPlaceholder = computed(() => {
  if (!selectedEntityType.value || selectedEntityType.value === 'all') {
    return 'Search clients, carriers, and wholesalers';
  }

  const typeOption = entityTypeOptions.find((option) => option.value === selectedEntityType.value);
  return typeOption ? `Search ${typeOption.label.toLowerCase()}` : 'Search...';
});

// -----
// methods
// -----

const getIconForType = (type: EntityType) => {
  const iconMap = {
    account: Building2Icon,
    carrier: BarChart3Icon,
    wholesaler: StoreIcon,
  };
  return iconMap[type];
};

const getChipClassForType = (result: UniversalSearchResult): string => {
  if (result.isProspect) {
      return 'tw:bg-blue-50 tw:text-blue-800';
    }

  const classMap = {
    account: 'tw:bg-blue-100 tw:text-blue-800',
    carrier: 'tw:bg-green-100 tw:text-green-800',
    wholesaler: 'tw:bg-orange-100 tw:text-orange-800',
  };
  return classMap[result.resultType];
};

const getIconColorForType = (type: EntityType): string => {
  const colorMap = {
    account: '#1e40af', // blue-800
    carrier: '#166534', // green-800
    wholesaler: '#9a3412', // orange-800
  };
  return colorMap[type];
};

const getTypeLabel = (result: UniversalSearchResult): string => {
  if (result.isProspect) {
    return 'Prospect';
  }

  const labelMap = {
    account: 'Client',
    carrier: 'Carrier',
    wholesaler: 'Wholesaler',
  };

  return labelMap[result.resultType];
};

const highlightText = (text: string, query: string): string => {
  return filters.highlight(text, query);
};

const sortResults = (results: UniversalSearchResult[], searchQuery: string): UniversalSearchResult[] => {
  if (!searchQuery || !searchQuery.trim()) {
    return results.sort((a, b) => a.name.localeCompare(b.name));
  }

  const query = searchQuery.toLowerCase().trim();

  return results.sort((a, b) => {
    const aName = a.name.toLowerCase();
    const bName = b.name.toLowerCase();

    const aStartsWithQuery = aName.startsWith(query);
    const bStartsWithQuery = bName.startsWith(query);

    const aContainsQuery = aName.includes(query);
    const bContainsQuery = bName.includes(query);

    // First priority: items that start with the search query
    if (aStartsWithQuery && !bStartsWithQuery) return -1;
    if (!aStartsWithQuery && bStartsWithQuery) return 1;

    // If both start with query or neither starts with query, check if they contain query
    if (aStartsWithQuery === bStartsWithQuery) {
      // Second priority: items that contain the search query
      if (aContainsQuery && !bContainsQuery) return -1;
      if (!aContainsQuery && bContainsQuery) return 1;

      // If both have same match priority, sort alphabetically
      return aName.localeCompare(bName);
    }

    return 0;
  });
};

const navigateToTab = (tab: TabConfig) => {
  router.push(tab.route);
};

const performSearch = async () => {
  if (!searchQuery.value.trim()) {
    results.value = [];
    hasSearched.value = false;
    return;
  }

  loading.value = true;
  hasSearched.value = true;

  try {
    const searchResults = await universalSearchService.search({
      search: searchQuery.value,
      entityTypes:
        selectedEntityType.value && selectedEntityType.value !== 'all' ? [selectedEntityType.value] : undefined,
    });
    results.value = sortResults(searchResults, searchQuery.value).slice(0, RESULT_LIMIT);
  } catch (error) {
    console.error('Search error:', error);
    results.value = [];
  } finally {
    loading.value = false;
  }
};

const onSearchInput = () => {
  // Debounce search
  clearTimeout(searchTimeout);
  searchTimeout = setTimeout(performSearch, 300);
};

const setHoveredResult = async (result: UniversalSearchResult) => {
  hoveredResult.value = result;

  // Load tabs for the hovered result
  if (result && store?.state.user) {
    try {
      const tabs = await getTabs(result.resultType, result.id.toString());
      entityTabs.value = tabs.value;
    } catch (error) {
      console.error('Error loading entity tabs:', error);
      entityTabs.value = [];
    }
  }
};

const navigateToResult = (result: UniversalSearchResult) => {
  const routeMap = {
    account: `/account/${result.id}/overview`,
    carrier: `/carrier/${result.id}/overview`,
    wholesaler: `/wholesaler-group/${result.id}/overview`,
  };

  const route = routeMap[result.resultType];
  if (route) {
    router.push(route);
  }
};

// -----
// watchers
// -----

watch(selectedEntityType, () => {
  if (searchQuery.value) {
    performSearch();
  }
});

watch(hoveredResult, (newResult) => {
  if (!newResult) {
    entityTabs.value = [];
  }
});

// -----
// click outside handling
// -----

const handleClickOutside = (event: MouseEvent) => {
  const target = event.target as HTMLElement;
  const searchContainer = searchResultsRef.value?.closest('.universal-search');

  // Don't close if clicking within the search container
  if (searchContainer && searchContainer.contains(target)) {
    return;
  }

  // Close search results
  searchQuery.value = '';
  results.value = [];
  hasSearched.value = false;
  hoveredResult.value = null;
};

// -----
// lifecycle
// -----

onMounted(() => {
  document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
});
</script>

<template>
  <div class="universal-search tw:mx-auto tw:max-w-3xl tw:shadow-md">
    <!-- Search Bar -->
    <div class="search-container tw:flex tw:items-center tw:gap-3 tw:rounded-lg tw:p-2">
      <!-- Type Filter Dropdown -->
      <div class="tw:relative tw:shrink-0">
        <SelectRoot v-model="selectedEntityType">
          <SelectTrigger
            as-child
            class="tw:inline-flex tw:h-[35px] tw:min-w-[120px] tw:items-center tw:justify-between tw:gap-[5px] tw:rounded-lg tw:border tw:bg-white tw:px-[15px] tw:text-xs tw:leading-none tw:text-gray-700 tw:shadow-xs tw:outline-none tw:hover:bg-gray-50 tw:focus:shadow-[0_0_0_2px] tw:focus:shadow-blue-500 tw:data-[state=open]:bg-gray-50">
            <button type="button" class="tw:flex tw:w-full tw:items-center tw:justify-between">
              <SelectValue placeholder="All Types" />
              <ChevronDown class="tw:h-3 tw:w-3 tw:text-gray-400" />
            </button>
          </SelectTrigger>

          <SelectContent
            class="tw:z-100 tw:min-w-[160px] tw:rounded-lg tw:border tw:bg-white tw:shadow-lg"
            position="popper"
            :side-offset="5">
            <SelectViewport class="tw:p-[5px]">
              <SelectItem
                value="all"
                class="tw:relative tw:flex tw:h-[25px] tw:cursor-pointer tw:select-none tw:items-center tw:rounded-[3px] tw:pl-[25px] tw:pr-[35px] tw:text-xs tw:leading-none tw:text-gray-700 tw:hover:bg-blue-100 tw:hover:outline-none tw:data-[highlighted]:bg-blue-100 tw:data-[highlighted]:outline-none">
                <SelectItemText>All Types</SelectItemText>
              </SelectItem>
              <SelectItem
                v-for="option in entityTypeOptions"
                :key="option.value"
                :value="option.value"
                class="tw:relative tw:flex tw:h-[25px] tw:cursor-pointer tw:select-none tw:items-center tw:gap-2 tw:rounded-[3px] tw:pl-[25px] tw:pr-[35px] tw:text-xs tw:leading-none tw:text-gray-700 tw:hover:bg-blue-100 tw:hover:outline-none tw:data-[highlighted]:bg-blue-100 tw:data-[highlighted]:outline-none">
                <component :is="getIconForType(option.value)" class="tw:h-3 tw:w-3" />
                <SelectItemText>{{ option.label }}</SelectItemText>
              </SelectItem>
            </SelectViewport>
          </SelectContent>
        </SelectRoot>
      </div>

      <!-- Search Input -->
      <div class="tw:relative tw:flex-1">
        <div class="tw:pointer-events-none tw:absolute tw:inset-y-0 tw:left-0 tw:flex tw:items-center tw:pl-3">
          <SearchIcon class="tw:h-4 tw:w-4 tw:text-gray-400" />
        </div>
        <input
          v-model="searchQuery"
          type="text"
          :placeholder="searchPlaceholder"
          class="tw:block tw:w-full tw:rounded-md tw:border tw:border-gray-300 tw:py-2 tw:pl-10 tw:pr-3 tw:text-sm tw:placeholder-gray-400 tw:focus:border-transparent tw:focus:outline-none tw:focus:ring-2 tw:focus:ring-blue-500"
          @keydown.enter="performSearch"
          @input="onSearchInput" />
      </div>
    </div>

    <!-- Search Results -->
    <div
      ref="searchResultsRef"
      class="search-results tw:absolute tw:left-0 tw:right-0 tw:top-full tw:z-50 tw:mt-1 tw:flex tw:h-[555px] tw:rounded-b-lg tw:border tw:border-t-0 tw:bg-white tw:shadow-lg"
      v-if="shouldShowResults"
      @mouseleave="hoveredResult = null">
      <!-- Results List -->
      <div class="results-list tw:w-1/2 tw:overflow-y-auto tw:border-r">
        <div v-if="loading" class="tw:p-4 tw:text-center">
          <div
            class="tw:inline-block tw:h-6 tw:w-6 tw:animate-spin tw:rounded-full tw:border-b-2 tw:border-blue-500"></div>
          <p class="tw:mt-2 tw:text-sm tw:text-gray-600">Searching...</p>
        </div>

        <div v-else-if="results.length === 0 && hasSearched" class="tw:p-4 tw:text-center tw:text-gray-500">
          <p class="tw:text-sm">No results found for "{{ searchQuery }}"</p>
        </div>

        <div v-else>
          <div
            v-for="result in results"
            :key="`${result.resultType}-${result.id}`"
            class="result-item tw:flex tw:cursor-pointer tw:items-center tw:gap-3 tw:border-b tw:p-3 tw:hover:bg-gray-50"
            @click="navigateToResult(result)"
            @mouseenter="setHoveredResult(result)">
            <!-- Type Icon -->
            <component :is="getIconForType(result.resultType)" class="tw:h-4 tw:w-4" :color="getIconColorForType(result.resultType)"/>

            <!-- Content -->
            <div class="tw:min-w-0 tw:flex-1">
              <div class="tw:truncate tw:text-sm tw:font-medium" v-html="highlightText(result.name, searchQuery)"></div>
              <div v-if="result.subtitle" class="tw:truncate tw:text-xs tw:text-gray-600">{{ result.subtitle }}</div>
              <div class="tw:mt-1 tw:flex tw:items-center tw:gap-2">
                <span
                  :class="`tw:inline-flex tw:items-center tw:rounded-full tw:px-3 tw:py-1 tw:text-xs tw:font-medium ${getChipClassForType(result)}`">
                  {{ getTypeLabel(result) }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Context Panel -->
      <div class="context-panel tw:flex tw:w-1/2 tw:flex-col tw:bg-gray-50 tw:p-3">
        <div v-if="hoveredResult" class="tw:flex tw:flex-1 tw:flex-col">
          <div class="tw:mb-3 tw:flex tw:w-full tw:items-center tw:gap-2">
            <component :is="getIconForType(hoveredResult.resultType)" class="tw:h-4 tw:w-4" :color="getIconColorForType(hoveredResult.resultType)" />
            <div>
              <h3 class="tw:text-sm tw:font-medium">{{ hoveredResult.name }}</h3>
              <p class="tw:text-xs tw:text-gray-600">{{ getTypeLabel(hoveredResult) }}</p>
            </div>
            <span v-if="hoveredResult.additionalInfo" class="tw:text-xs tw:text-gray-500 tw:self-start tw:mr-0 tw:ml-auto">{{ hoveredResult.additionalInfo }}</span>
          </div>

          <!-- Tabs Navigation -->
          <div class="tw:flex tw:min-h-[200px] tw:flex-1 tw:flex-col tw:rounded tw:border tw:bg-white tw:p-2 tw:px-2">
            <h4 class="tw:mb-2 tw:text-xs tw:font-medium tw:text-gray-700">Quick Navigation</h4>
            <div class="tw:flex-1 tw:space-y-1 tw:overflow-y-auto">
              <template v-if="!loadingTabs">
                <button
                  v-for="tab in entityTabs"
                  :key="tab.key"
                  v-show="tab.visible"
                  class="tw:flex tw:w-full tw:cursor-pointer tw:items-center tw:gap-2 tw:rounded tw:p-2 tw:text-left tw:text-xs tw:hover:bg-gray-50 tw:focus:bg-blue-100 tw:focus:outline-none"
                  @click="navigateToTab(tab)">
                  <component :is="tab.icon" v-if="tab.icon" class="tw:h-3 tw:w-3 tw:text-gray-500" />
                  <span class="tw:text-gray-700">{{ tab.label }}</span>
                </button>
                <div v-if="entityTabs.length === 0" class="tw:flex tw:items-center tw:justify-center tw:py-8">
                  <p class="tw:text-xs tw:text-gray-500">No navigation items available</p>
                </div>
              </template>
            </div>
          </div>
        </div>

        <div v-else class="tw:flex tw:h-full tw:items-center tw:justify-center tw:text-center">
          <div class="tw:text-gray-500">
            <SearchIcon class="tw:mx-auto tw:mb-2 tw:h-8 tw:w-8 tw:text-gray-400" />
            <p class="tw:text-sm">Hover over a result to see more details</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
@reference "@/assets/styles/global.css";

.universal-search {
  @apply tw:relative tw:rounded-lg tw:border tw:bg-white tw:shadow-xs;
}

.search-container {
  @apply tw:bg-gray-50;
}

.result-item:last-child {
  @apply tw:border-b-0;
}

.type-filter :deep(.v-field) {
  @apply tw:min-h-0;
}
</style>

<!-- Global styles to prevent reka-ui body padding -->
<style>
/* Prevent reka-ui from adding body padding when dropdown opens */
body[style*='padding-right'] {
  padding-right: 0px !important;
}
</style>
